import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { FormCard, SectionHeader, ToggleField } from "@fawkes/ui";

import type { ModuleInfo } from "../api/modules";

// Modulebeheer (Epic 20) Jmix-exit: the React replacement for the Jmix ModuleManagementView. An admin
// toggles functional modules organisation-wide; the list is rendered from GET /api/modules and each
// toggle PUTs /api/modules/{key} (gated ROLE_MODULE_ADMIN). System/infra modules (userManageable=false)
// are env-governed and hidden, exactly like the Jmix screen. A sub-module's toggle is greyed when an
// ancestor is off (cascade preview) — its own value is preserved; effective visibility is enforced by
// the server's cascade.

function depthOf(key: string, parentByKey: Map<string, string | null>): number {
  let depth = 0;
  let parent = parentByKey.get(key) ?? null;
  while (parent && depth < 50) {
    depth += 1;
    parent = parentByKey.get(parent) ?? null;
  }
  return depth;
}

function ancestorsEnabled(key: string, parentByKey: Map<string, string | null>, ownByKey: Map<string, boolean>): boolean {
  let parent = parentByKey.get(key) ?? null;
  let guard = 0;
  while (parent && guard < 50) {
    if (!ownByKey.get(parent)) return false;
    parent = parentByKey.get(parent) ?? null;
    guard += 1;
  }
  return true;
}

export function ModulebeheerScreen() {
  const t = useTranslate();
  const { data, isLoading, isError, refetch } = useCustom<ModuleInfo[]>({ url: "modules", method: "get" });
  const { mutate, isLoading: saving } = useCustomMutation();

  if (isLoading) return <p className="fawkes-muted">{t("common.loading")}</p>;
  if (isError) return <p className="fawkes-error">{t("moduleManagementView.loadFailed")}</p>;

  const modules = (data?.data ?? []).filter((m) => m.userManageable);
  const parentByKey = new Map(modules.map((m) => [m.key, m.parentKey]));
  const ownByKey = new Map(modules.map((m) => [m.key, m.ownEnabled]));

  const onToggle = (key: string, enabled: boolean) => {
    mutate(
      { url: `modules/${key}`, method: "put", values: { enabled }, successNotification: false, errorNotification: false },
      { onSuccess: () => refetch(), onError: () => refetch() },
    );
  };

  return (
    <FormCard>
      <SectionHeader>{t("moduleManagementView.title")}</SectionHeader>
      {/* Source parity: the Jmix module-management view has no intro paragraph. */}
      <div className="fawkes-module-list">
        {modules.map((m) => {
          const indent = depthOf(m.key, parentByKey);
          const ancestorOn = ancestorsEnabled(m.key, parentByKey, ownByKey);
          return (
            <div
              key={m.key}
              className="fawkes-module-row"
              style={indent > 0 ? { marginLeft: `${indent * 1.75}rem` } : undefined}
            >
              <ToggleField
                label={t(m.labelKey)}
                checked={m.ownEnabled}
                disabled={saving || !ancestorOn}
                onChange={(e) => onToggle(m.key, e.target.checked)}
              />
              <p className="fawkes-module-row__desc fawkes-muted">{t(m.descriptionKey)}</p>
            </div>
          );
        })}
      </div>
    </FormCard>
  );
}
